import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, Fab, FormControlLabel, IconButton, Paper, Stack, Switch, Tab, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { Controller, useForm } from 'react-hook-form';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { animated, useSpring } from '@react-spring/web';

import { MickeyIcon } from '/packages/shared-components/Header/FavIcon';
import TabNav from '/apps/portal/src/components/navigation/TabNav/TabNav';
import { TDocsPermissions, TMiraPermissions, TUserPermissions, hasCodeAccess } from '../utils/user';
import { formatWorkstream, getEnabledElements, hasMagic } from '../utils/util';
import BuildVersion from './BuildVersion';

const BuildInfo = ({ build }: { build?: Record<TBuildName, TBuild> }) => {
  return (
    <>
      {build?.webapp && <BuildVersion build={build.webapp} name={'webapp'} />}
      {build?.mira && <BuildVersion build={build.mira} name={'mira'} />}
      {build?.api && <BuildVersion build={build.api} name={'api'} />}
      {build?.adk && <BuildVersion build={build.adk} name={'adk'} />}
    </>
  );
};

const WorkstreamsSection = ({ control, workstreams }: { control: any; workstreams: string[] }) => (
  <>
    <Divider textAlign="left">Workstreams</Divider>
    <Controller
      control={control}
      name="workstreams"
      render={({ field }) => (
        <>
          {workstreams.map((ws) => (
            <FormControlLabel
              key={`sw-ws-${ws}`}
              control={<Switch color="primary" checked={field.value.includes(ws)} />}
              onChange={(_event, checked) => {
                field.onChange(checked ? [...field.value, ws] : field.value.filter((w: string) => w !== ws));
              }}
              label={formatWorkstream(ws)}
              slotProps={{ typography: { width: '100%' } }}
              labelPlacement="start"
            />
          ))}
        </>
      )}
    />
  </>
);

const MagicFormButtons = ({ onReset, onSubmit }: { onReset: () => void; onSubmit?: () => void }) => (
  <Stack direction="row" justifyContent="space-between">
    <IconButton onClick={onReset} color="warning">
      <SettingsBackupRestoreIcon />
    </IconButton>
    <IconButton type={onSubmit ? 'button' : 'submit'} onClick={onSubmit} color="success">
      <AutoFixHighIcon />
    </IconButton>
  </Stack>
);

type TPortalMagicFormProps = {
  defaultValues: TUserPermissions;
  onSuccess?: () => void;
  onClose: () => void;
  onApplyChanges: () => void;
  storageKey: string;
  workstreams: string[];
  build?: Record<TBuildName, TBuild>;
};

const PortalMagicForm = ({
  defaultValues,
  onSuccess,
  onClose,
  onApplyChanges,
  storageKey,
  workstreams,
  build,
}: TPortalMagicFormProps) => {
  const [activeTab, setActiveTab] = useState('permissions');
  const { control, handleSubmit } = useForm<TUserPermissions>({ defaultValues });

  const onSubmit = (data: TUserPermissions) => {
    sessionStorage.setItem(storageKey, JSON.stringify(data));
    onApplyChanges();
    if (onSuccess) onSuccess();
    onClose();
  };

  const onReset = () => {
    sessionStorage.removeItem(storageKey);
    onApplyChanges();
    onClose();
  };

  return (
    <Paper elevation={8} sx={{ minWidth: '300px', p: 2, width: '540px' }}>
      <Stack gap={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Magic Form</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <TabContext value={activeTab}>
          <TabNav onChange={(val) => setActiveTab(val)}>
            <Tab label="Permissions" value="permissions" />
            {build && <Tab label="Versions" value="versions" />}
          </TabNav>
          <TabPanel value="permissions" sx={{ p: 0, pt: 2 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack sx={{ maxHeight: '50vh', overflowX: 'clip', overflowY: 'auto', pr: 1, mr: -1 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2 }}>
                  <Stack>
                    <WorkstreamsSection control={control} workstreams={workstreams} />
                    <Divider textAlign="left">Access Management</Divider>
                    <Controller
                      control={control}
                      name="accessManagement.admin"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.accessManagement.admin} />}
                          label="Admin"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Divider textAlign="left">Perks</Divider>
                    <Controller
                      control={control}
                      name="perks.admin"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.perks.admin} />}
                          label="Admin"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                  </Stack>
                  <Stack>
                    <Divider textAlign="left">ADK</Divider>
                    <Controller
                      control={control}
                      name="adk.createProject"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.adk.admin} />}
                          label="Create Project"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="adk.requestProject"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.adk.requestProject} />}
                          label="Request Project"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="adk.reviewProject"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.adk.reviewProject} />}
                          label="Review Project"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="adk.changesRequested"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.adk.changesRequested} />}
                          label="Changes Requested Project"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="adk.codeAccess.*"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={hasCodeAccess(defaultValues, '')} />}
                          label="Code Access"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Divider textAlign="left">Android</Divider>
                    <Controller
                      control={control}
                      name="android.admin"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.android.admin} />}
                          label="Admin"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Divider textAlign="left">API</Divider>
                    <Controller
                      control={control}
                      name="api.admin"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.api.admin} />}
                          label="Admin"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="api.sku"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={<Switch color="primary" defaultChecked={defaultValues.api.sku} />}
                          label="SKU"
                          slotProps={{ typography: { width: '100%' } }}
                          labelPlacement="start"
                        />
                      )}
                    />
                  </Stack>
                </Box>
              </Stack>
              <MagicFormButtons onReset={onReset} />
            </form>
          </TabPanel>
          <TabPanel value="versions" sx={{ p: 0, pt: 2 }}>
            <BuildInfo build={build} />
          </TabPanel>
        </TabContext>
      </Stack>
    </Paper>
  );
};

type TDocsMagicFormProps = {
  storageKey: string;
  workstreams: string[];
  defaultValues: TDocsPermissions;
  onApplyChanges: () => void;
  onClose: () => void;
  build?: Record<TBuildName, TBuild>;
};

const DocsMagicForm = ({
  storageKey,
  workstreams,
  defaultValues,
  onApplyChanges,
  onClose,
  build,
}: TDocsMagicFormProps) => {
  const [activeTab, setActiveTab] = useState('permissions');
  const { control, handleSubmit } = useForm<TDocsPermissions>({ defaultValues });

  const onReset = () => {
    sessionStorage.removeItem(storageKey);
    onApplyChanges();
    onClose();
  };

  const onSubmit = (data: TDocsPermissions) => {
    sessionStorage.setItem(storageKey, JSON.stringify(data));
    onApplyChanges();
    onClose();
  };

  return (
    <Paper elevation={3} sx={{ minWidth: '300px', p: 2 }}>
      <TabContext value={activeTab}>
        <TabNav onChange={(val) => setActiveTab(val)}>
          <Tab label="Permissions" value="permissions" />
          {build && <Tab label="Versions" value="versions" />}
        </TabNav>
        <TabPanel value="permissions" sx={{ p: 0, pt: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack sx={{ maxHeight: '50vh', overflowX: 'clip', overflowY: 'auto', pr: 1, mr: -1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2 }}>
                <Stack>
                  <WorkstreamsSection control={control} workstreams={workstreams} />
                </Stack>
                <Stack>
                  <Divider textAlign="left">Draft/Modified Content</Divider>
                  <Controller
                    control={control}
                    name="draft"
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch color="primary" checked={field.value} onChange={field.onChange} />}
                        label="Show"
                        slotProps={{ typography: { width: '100%' } }}
                        labelPlacement="start"
                      />
                    )}
                  />
                </Stack>
              </Box>
            </Stack>
            <MagicFormButtons onReset={onReset} />
          </form>
        </TabPanel>
        <TabPanel value="versions" sx={{ p: 0, pt: 2 }}>
          <BuildInfo build={build} />
        </TabPanel>
      </TabContext>
    </Paper>
  );
};

type TMiraMagicForm = {
  build?: Record<TBuildName, TBuild>;
  storageKey: string;
  workstreams: string[];
  defaultValues: TMiraPermissions;
  onApplyChanges: () => void;
  onClose: () => void;
};

const MiraMagicForm = ({ storageKey, build, defaultValues, workstreams, onApplyChanges, onClose }: TMiraMagicForm) => {
  const [activeTab, setActiveTab] = useState('versions');
  const { control, handleSubmit } = useForm<TMiraPermissions>({ defaultValues });

  const onReset = () => {
    sessionStorage.removeItem(storageKey);
    onApplyChanges();
    onClose();
  };

  const onSubmit = (data: TMiraPermissions) => {
    sessionStorage.setItem(storageKey, JSON.stringify(data));
    onApplyChanges();
    onClose();
  };

  return (
    <Paper elevation={3} sx={{ minWidth: '300px', p: 2 }}>
      <TabContext value={activeTab}>
        <TabNav onChange={(val) => setActiveTab(val)}>
          {/* <Tab label="Permissions" value="permissions" /> */}
          {build && <Tab label="Versions" value="versions" />}
        </TabNav>
        {/* <TabPanel value="permissions" sx={{ p: 0, pt: 2 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack sx={{ maxHeight: '50vh', overflowX: 'clip', overflowY: 'auto', pr: 1, mr: -1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2 }}>
                <Stack>
                  <WorkstreamsSection control={control} workstreams={workstreams} />
                </Stack>
              </Box>
            </Stack>
            <MagicFormButtons onReset={onReset} />
          </form>
        </TabPanel> */}
        <TabPanel value="versions" sx={{ p: 0, pt: 2 }}>
          <BuildInfo build={build} />
        </TabPanel>
      </TabContext>
    </Paper>
  );
};

interface MagicFormProps {
  view: 'partner' | 'docs' | 'mira';
  storageKey: string;
  defaultValues: TUserPermissions | TDocsPermissions | TMiraPermissions;
  setLastMagic: React.Dispatch<React.SetStateAction<Date>>;
  workstreams: string[];
}

export const MagicForm = ({ view, storageKey, defaultValues, setLastMagic, workstreams }: MagicFormProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const enabled = hasMagic();
  const [build, setBuild] = useState<Record<string, TBuild>>({});

  useEffect(() => {
    const fetchBuildInfo = (url: string): Promise<TBuild> =>
      fetch(url)
        .then((res) => (res.ok ? res.json() : { error: `HTTP ${res.status}` }))
        .catch((err) => ({ error: err.message }));

    const adkBaseUrl = (process.env.REACT_APP_WEBSOCKET_URI || '').replace(/^wss?:/, 'https:');

    Promise.all([
      fetchBuildInfo(process.env.NODE_ENV === 'development' ? '/build-info.json' : '/app/build-info.json'),
      fetchBuildInfo('/support/build-info.json'),
      fetchBuildInfo(`${process.env.REACT_APP_API_BASE_URI}/build-info.json`),
      fetchBuildInfo(`${adkBaseUrl}/build-info.json`),
    ]).then(([webapp, mira, api, adk]) => {
      setBuild({ webapp, mira, api, adk });
    });
  }, []);

  const handleApplyChange = () => {
    setLastMagic(new Date());
  };

  const FAB_SIZE = 56;
  const MARGIN = 24;
  const containerRef = useRef<HTMLDivElement>(null);

  const getClosedPosition = () => ({
    x: window.innerWidth / 2 - MARGIN - FAB_SIZE,
    y: window.innerHeight / 2 - MARGIN - FAB_SIZE,
  });

  const [springs, api] = useSpring(() => getClosedPosition());

  const updatePosition = (isOpen: boolean) => {
    if (isOpen && containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      api.start({ x: -width / 2, y: -height / 2 });
    } else {
      api.start(getClosedPosition());
    }
  };

  useLayoutEffect(() => {
    updatePosition(open);
  }, [open]);

  useEffect(() => {
    const handleResize = () => updatePosition(open);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open]);

  if (!enabled) return null;

  // Above FloatingChatAssistant (z 1300–1301) so the Magic FAB is reachable; keep below MUI tooltips if possible.
  const zMagic = 1302;

  return (
    <animated.div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        zIndex: zMagic,
        x: springs.x,
        y: springs.y,
      }}
    >
      <Stack alignItems="center">
        <Fab onClick={() => setOpen((o) => !o)} color="default" sx={{ zIndex: 1 }}>
          <Box height="20px" mt={0.25}>
            <MickeyIcon />
          </Box>
        </Fab>
        {open && view === 'partner' && (
          <Box mt="-28px">
            <PortalMagicForm
              defaultValues={defaultValues as TUserPermissions}
              storageKey={storageKey}
              onApplyChanges={handleApplyChange}
              onClose={() => setOpen(false)}
              workstreams={workstreams}
              build={build}
            />
          </Box>
        )}
        {open && view === 'docs' && (
          <Box mt="-28px">
            <DocsMagicForm
              defaultValues={defaultValues as TDocsPermissions}
              storageKey={storageKey}
              onApplyChanges={handleApplyChange}
              onClose={() => setOpen(false)}
              workstreams={workstreams}
              build={build}
            />
          </Box>
        )}
        {open && view === 'mira' && (
          <Box mt="-28px">
            <MiraMagicForm
              defaultValues={defaultValues as TMiraPermissions}
              storageKey={storageKey}
              onApplyChanges={handleApplyChange}
              onClose={() => setOpen(false)}
              workstreams={workstreams}
              build={build}
            />
          </Box>
        )}
      </Stack>
    </animated.div>
  );
};

export type TBuild = TBuildResult | TFetchError;

export type TBuildResult = {
  name: string;
  version: string;
  lastCommitTime: string;
  gitSha: string;
  gitBranch: string;
  gitTags: string[];
  buildTime: string;
};

type TFetchError = { error: string };

export type TBuildName = 'webapp' | 'mira' | 'api' | 'adk';
